import { Anchor, Box, Button, Group, Stack, Text } from "@mantine/core"
import { MapTrifoldIcon } from "@phosphor-icons/react"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { DotPill, Tone } from "../delivery/atoms"
import type { IDeliveryRoute } from "../../apis/delivery-route"
import classes from "./dn-detail.module.css"

const ROUTE_TONE: Record<string, Tone> = { PLANNED: "grey", LOADING: "amber", IN_PROGRESS: "blue", COMPLETED: "green", CANCELLED: "grey" }
const STOP_TONE: Record<string, Tone> = { PENDING: "grey", ARRIVED: "blue", COMPLETED: "green", FAILED: "red", SKIPPED: "grey" }

/** «Маршрут» tab: the route(s) holding this DN, progress, and the stop list with this DN's stop marked. */
export function DnRoutePanel({ dnId, routes, isLoading }: Readonly<{ dnId: string | null; routes: IDeliveryRoute[]; isLoading: boolean }>) {
  const { t } = useTranslation("delivery")

  if (isLoading) {
    return (
      <Text className={classes.hint} ta="center" py="md">
        {t("dnDetail.loading")}
      </Text>
    )
  }
  if (routes.length === 0) return <Text className={classes.hint}>{t("dnDetail.noRouteYet")}</Text>

  return (
    <Stack gap="md">
      {routes.map((route) => {
        const totalStops = route.totalStops ?? route.stops?.length ?? 0
        const handled = (route.completedStops ?? 0) + (route.failedStops ?? 0) + (route.skippedStops ?? 0)
        const pct = totalStops > 0 ? Math.min(100, (handled / totalStops) * 100) : 0
        const thisStop = route.stops?.find((s) => s.deliveryNoteId === dnId)
        const facts = [
          `${t("dnDetail.date")}: ${route.plannedDate ? dayjs(route.plannedDate).format("YYYY-MM-DD") : "-"}`,
          t("dnDetail.stopsProgress", { done: handled, total: totalStops }),
          route.totalDistanceKm ? t("dnDetail.km", { n: route.totalDistanceKm }) : null,
          route.plannedDurationMinutes != null ? t("dnDetail.minutes", { n: route.plannedDurationMinutes }) : null,
        ].filter(Boolean)
        return (
          <Box key={route.id} className={classes.routeCard}>
            <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
              <Stack gap={6}>
                <Group gap="sm" align="center">
                  <Anchor component={Link} to={`/delivery-routes/${route.id}`} className={`${classes.mono} ${classes.link}`} translate="no">
                    {route.routeNumber}
                  </Anchor>
                  <DotPill tone={ROUTE_TONE[route.status] ?? "grey"}>{t(`dnDetail.routeStatus.${route.status}`, { defaultValue: "-" })}</DotPill>
                  {thisStop && <DotPill tone="grey">{t("dnDetail.stopLabel", { n: thisStop.stopNumber })}</DotPill>}
                </Group>
                <Text className={classes.label}>{facts.join(" · ")}</Text>
                <Group gap="sm" maw={320} wrap="nowrap">
                  <Box className={classes.progress} flex={1}>
                    <Box className={classes.progressBar} w={`${pct}%`} />
                  </Box>
                  <Text className={classes.label} miw={40}>
                    {Math.round(pct)}%
                  </Text>
                </Group>
              </Stack>
              <Button component={Link} to={`/delivery-routes/${route.id}`} variant="default" rightSection={<MapTrifoldIcon size={16} />}>
                {t("dnDetail.jumpToRoute")}
              </Button>
            </Group>

            {route.stops && route.stops.length > 0 && (
              <Stack gap={0} mt="md">
                {route.stops.slice(0, 10).map((stop) => (
                  <Group key={stop.id} className={classes.stopRow} data-current={stop.deliveryNoteId === dnId || undefined} gap="sm" wrap="nowrap">
                    <Box className={classes.stopNo}>{stop.stopNumber}</Box>
                    <Text fz="var(--nw-fz-body)" lineClamp={1} flex={1} miw={0} fw={stop.deliveryNoteId === dnId ? 700 : 400}>
                      {stop.address || stop.dn?.receiverBusinessName || t("dnDetail.stopLabel", { n: stop.stopNumber })}
                    </Text>
                    <DotPill tone={STOP_TONE[stop.status] ?? "grey"}>{t(`dnDetail.stopStatus.${stop.status}`, { defaultValue: "-" })}</DotPill>
                  </Group>
                ))}
              </Stack>
            )}
          </Box>
        )
      })}
    </Stack>
  )
}
